import {
	Button,
	DrawerClose,
	DrawerFooter,
	Separator,
	TintedBackgroundIcon,
} from "@monbits/ui";
import { Check, ShieldAlert } from "lucide-react";
import * as React from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { toast } from "sonner";
import CopyButton from "src/components/copy-button/copy-button";
import DetailRow from "src/components/detail-row/detail-row";
import { EntityDrawer } from "src/components/entity-drawer";
import { StatusBadge } from "src/components/status-badge";
import alertsQueries from "src/queries/alerts-queries";
import type { SystemAlert } from "src/types";
import { getAlertCategoryConfig } from "src/utils/entity-config";
import { getSeverityConfig, getStatusConfig } from "src/utils/status-config";
import type { AlertsOutletContext } from "./alerts-list";

const formatDateTime = (value?: string) => {
	if (!value) return "—";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return value;
	return date.toLocaleString();
};

export function AlertDetail() {
	const { alertId } = useParams<{ alertId: string }>();
	const [localAlert, setLocalAlert] = React.useState<SystemAlert | null>(null);

	const outletContext = useOutletContext<AlertsOutletContext | undefined>();
	const alerts = outletContext?.alerts ?? [];
	const isLoading = outletContext?.isLoading ?? false;

	const acknowledgeMutation = alertsQueries.acknowledgeAlert();
	const resolveMutation = alertsQueries.resolveAlert();

	const alert = React.useMemo(() => {
		if (!alertId) return null;
		return alerts.find((a) => a.id === Number(alertId)) || null;
	}, [alertId, alerts]);

	React.useEffect(() => {
		if (alert) {
			setLocalAlert(alert);
		}
	}, [alert]);

	const updateLocalAlert = React.useCallback((updatedAlert: SystemAlert) => {
		setLocalAlert(updatedAlert);
	}, []);

	const handleAcknowledge = React.useCallback(() => {
		if (!alertId) return;

		acknowledgeMutation.mutate(Number(alertId), {
			onSuccess: (updatedAlert) => {
				toast.success("Alert acknowledged", {
					description: updatedAlert.code,
				});
				updateLocalAlert(updatedAlert);
			},
			onError: () => {
				toast.error("Failed to acknowledge alert", {
					description: "Please try again",
				});
			},
		});
	}, [alertId, acknowledgeMutation, updateLocalAlert]);

	const handleResolve = React.useCallback(() => {
		if (!alertId) return;

		resolveMutation.mutate(Number(alertId), {
			onSuccess: (updatedAlert) => {
				toast.success("Alert resolved", {
					description: updatedAlert.code,
				});
				updateLocalAlert(updatedAlert);
			},
			onError: () => {
				toast.error("Failed to resolve alert", {
					description: "Please try again",
				});
			},
		});
	}, [alertId, resolveMutation, updateLocalAlert]);

	const isMutating = acknowledgeMutation.isPending || resolveMutation.isPending;
	const displayAlert = localAlert || alert;
	const metadata = displayAlert?.metadata ?? {};

	return (
		<EntityDrawer
			title={`Alert ${alertId ? `#${alertId}` : ""}`}
			isLoading={isLoading}
			isNotFound={!isLoading && !displayAlert}
			notFoundMessage="Alert not found"
			parentPath="/alerts"
		>
			{displayAlert && (
				<>
					<div className="flex-1 overflow-y-auto px-4 py-6">
						<div className="space-y-6">
							{/* Summary Section */}
							<div className="space-y-2">
								<h3 className="text-sm font-semibold">Summary</h3>
								<div className="rounded-lg border bg-muted/40 p-4 text-sm leading-relaxed">
									{displayAlert.message}
								</div>
							</div>

							<Separator />

							{/* Status & Severity */}
							<div>
								<h3 className="text-sm font-semibold mb-3">
									Status & Severity
								</h3>
								<div className="space-y-2">
									<DetailRow
										label="Severity"
										value={(() => {
											const config = getSeverityConfig(displayAlert.severity);
											return (
												<StatusBadge type={config.badgeType} icon={config.icon}>
													{config.label}
												</StatusBadge>
											);
										})()}
									/>
									<DetailRow
										label="Status"
										value={(() => {
											const config = getStatusConfig(displayAlert.status);
											return (
												<StatusBadge type={config.badgeType} icon={config.icon}>
													{config.label}
												</StatusBadge>
											);
										})()}
									/>
									<DetailRow
										label="Category"
										value={
											displayAlert.category ? (
												<div className="flex items-center gap-2">
													<TintedBackgroundIcon
														icon={
															getAlertCategoryConfig(displayAlert.category).icon
														}
														color={
															getAlertCategoryConfig(displayAlert.category)
																.color
														}
														size="sm"
													/>
													<span>
														{
															getAlertCategoryConfig(displayAlert.category)
																.label
														}
													</span>
												</div>
											) : (
												"—"
											)
										}
									/>
								</div>
							</div>

							<Separator />

							{/* Identifiers */}
							<div>
								<h3 className="text-sm font-semibold mb-3">Identifiers</h3>
								<div className="space-y-2">
									<DetailRow
										label="Code"
										value={
											<>
												<span className="font-mono text-xs">
													{displayAlert.code}
												</span>
												<CopyButton
													value={displayAlert.code}
													label="Alert code"
												/>
											</>
										}
									/>
									<DetailRow
										label="Alert ID"
										value={
											<>
												<span className="font-mono text-xs">
													{displayAlert.id}
												</span>
												<CopyButton
													value={String(displayAlert.id)}
													label="Alert ID"
												/>
											</>
										}
									/>
								</div>
							</div>

							<Separator />

							{/* Occurrence Info */}
							<div>
								<h3 className="text-sm font-semibold mb-3">Occurrence Info</h3>
								<div className="space-y-2">
									<DetailRow
										label="Occurrences"
										value={
											<span className="font-mono tabular-nums">
												{displayAlert.occurrenceCount}
											</span>
										}
									/>
									<DetailRow
										label="Created at"
										value={formatDateTime(displayAlert.createdAt)}
									/>
									<DetailRow
										label="Last triggered"
										value={formatDateTime(displayAlert.lastTriggeredAt)}
									/>
									{displayAlert.acknowledgedAt && (
										<DetailRow
											label="Acknowledged at"
											value={formatDateTime(displayAlert.acknowledgedAt)}
										/>
									)}
									{displayAlert.resolvedAt && (
										<DetailRow
											label="Resolved at"
											value={formatDateTime(displayAlert.resolvedAt)}
										/>
									)}
								</div>
							</div>

							<Separator />

							{/* Related Entity */}
							<div>
								<h3 className="text-sm font-semibold mb-3">Related Entity</h3>
								<div className="space-y-2">
									<DetailRow
										label="Merchant ID"
										value={
											displayAlert.merchantId ? (
												<>
													<span className="font-mono text-xs">
														{displayAlert.merchantId}
													</span>
													<CopyButton
														value={String(displayAlert.merchantId)}
														label="Merchant ID"
													/>
												</>
											) : (
												"—"
											)
										}
									/>
									<DetailRow
										label="Entity Type"
										value={displayAlert.entityType || "—"}
									/>
									{displayAlert.entityId && (
										<DetailRow
											label="Entity ID"
											value={
												<>
													<span className="font-mono text-xs">
														{displayAlert.entityId}
													</span>
													<CopyButton
														value={String(displayAlert.entityId)}
														label="Entity ID"
													/>
												</>
											}
										/>
									)}
								</div>
							</div>

							{/* Metadata Section */}
							{Object.keys(metadata).length > 0 && (
								<>
									<Separator />
									<div>
										<h3 className="text-sm font-semibold mb-3">Metadata</h3>
										<div className="space-y-2 text-sm">
											{Object.entries(metadata).map(([key, value]) => (
												<div
													key={key}
													className="flex flex-col gap-1 rounded border bg-muted/30 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
												>
													<span className="font-medium text-muted-foreground shrink-0">
														{key}
													</span>
													<span className="truncate font-mono text-xs sm:text-right">
														{value}
													</span>
												</div>
											))}
										</div>
									</div>
								</>
							)}
						</div>
					</div>

					<Separator />

					<DrawerFooter className="flex flex-col gap-3">
						<div className="flex flex-col gap-1 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:gap-3">
							<span>
								Last triggered: {formatDateTime(displayAlert.lastTriggeredAt)}
							</span>
							<span>Alert ID: {displayAlert.id}</span>
						</div>
						<div className="flex flex-col gap-2 sm:flex-row">
							<DrawerClose asChild>
								<Button variant="outline" className="flex-1">
									Close
								</Button>
							</DrawerClose>
							{displayAlert.status === "open" && (
								<Button
									variant="secondary"
									className="flex-1"
									disabled={isMutating}
									onClick={handleAcknowledge}
								>
									<Check className="mr-2 h-4 w-4" /> Acknowledge
								</Button>
							)}
							{displayAlert.status !== "resolved" && (
								<Button
									variant="destructive"
									className="flex-1"
									disabled={isMutating}
									onClick={handleResolve}
								>
									<ShieldAlert className="mr-2 h-4 w-4" /> Resolve
								</Button>
							)}
						</div>
					</DrawerFooter>
				</>
			)}
		</EntityDrawer>
	);
}
